import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Trash2,
  MapPin, ExternalLink, Package, X, Calculator,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  DESIGN_COAT_PRODUCTS,
  PIGMENTS_PRODUCTS,
  TOP_COAT_PRODUCTS,
} from '@/components/proepoxy/productData';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function MetallicStep5({ state, priority, setPriority, currentStep, goToStep, navigate, onExit }) {
  const {
    primerAreas, setPrimerAreas, designCoatSelections, pigmentSelections, topCoatSelections,
    steps,
    addPrimerArea, removePrimerArea, updatePrimerArea, updateAreaPackQty,
    updateAreaField, updateAreaFloorPrepQty,
    initAreaTopCoat, updateAreaTopCoatFinish, updateAreaTopCoatPackQty,
    getAreaTopCoatSubtotal, getAreaCoverage, getAreaSubtotal,
    initializeDesignCoatForArea, updateDesignCoatProduct,
    updateDesignCoatPackQtyLegacy, updateCrystalUnitsLegacy,
    getDesignCoatSubtotal, getDesignCoatCoverage,
    selectTopCoatProduct, updateTopCoatFinishLegacy, updateTopCoatPackQtyLegacy,
    getTopCoatSubtotal, getTopCoatCoverage,
    addMetallicPowder, updateMetallicPowderQty, removeMetallicPowder,
    addPigmentPaste, removePigmentPaste, updatePigmentPastePackQty,
    addGlitter, removeGlitter, updateGlitterPackQty,
    addSpray, updateSprayQty, getPigmentsSubtotal,
    calculateTotal, getItemCount,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-[#27292E]">Pigments, Glitters & Sprays</h2>
                    <span className="text-sm text-gray-500">
                      Total area: {primerAreas.reduce((sum, a) => sum + (parseInt(a.m2) || 0), 0)}m²
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Add finishing effects to your epoxy floor. All items are optional.
                  </p>

                  {/* Metallic Pigment Powders */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3 pb-2 border-b">
                      <h3 className="font-bold text-[#27292E]">Metallic Pigment Powders</h3>
                      <a
                        href="https://customer-assets.emergentagent.com/job_76625416-d43e-411a-b4d0-3b73c58f517c/artifacts/q10lzgvh_metallic_pigment_chart_compressed.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-[#7A0B7A] hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Colour Chart
                      </a>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {PIGMENTS_PRODUCTS.metallicPowder.colors.map(color => {
                        const hasColor = pigmentSelections.metallicPowders.some(p => p.color === color);
                        return (
                          <button
                            key={color}
                            onClick={() => addMetallicPowder(color, '100g')}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              hasColor 
                                ? 'bg-[#7A0B7A] text-white' 
                                : 'bg-gray-100 hover:bg-[#7A0B7A]/10'
                            }`}
                          >
                            {color}
                          </button>
                        );
                      })}
                    </div>
                    {/* Group powders by color */}
                    {(() => {
                      const colorGroups = {};
                      pigmentSelections.metallicPowders.forEach(p => {
                        if (!colorGroups[p.color]) colorGroups[p.color] = {};
                        colorGroups[p.color][p.size] = p.quantity;
                      });
                      const colors = Object.keys(colorGroups);
                      if (colors.length === 0) return null;
                      
                      return (
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                          {colors.map(color => (
                            <div key={color} className="p-4 bg-white rounded-lg border-2 border-[#7A0B7A]/20">
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-medium text-[#27292E]">{color}</span>
                                <button
                                  onClick={() => removeMetallicPowder(color)}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              {/* 100g row */}
                              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">100g - £8.99</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => updateMetallicPowderQty(color, '100g', -1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="w-8 text-center font-bold">{colorGroups[color]['100g'] || 0}</span>
                                  <button
                                    onClick={() => addMetallicPowder(color, '100g')}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              {/* 1kg row */}
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-600">1kg - £74.99</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => updateMetallicPowderQty(color, '1kg', -1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="w-8 text-center font-bold">{colorGroups[color]['1kg'] || 0}</span>
                                  <button
                                    onClick={() => addMetallicPowder(color, '1kg')}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              {/* Total for this color */}
                              <div className="mt-2 pt-2 border-t border-gray-200 flex justify-end">
                                <span className="font-bold text-[#7A0B7A]">
                                  Total: £{(
                                    (colorGroups[color]['100g'] || 0) * 8.99 + 
                                    (colorGroups[color]['1kg'] || 0) * 74.99
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Pigment Paste */}
                  <div className="mb-6">
                    <h3 className="font-bold text-[#27292E] mb-3 pb-2 border-b">Pigment Paste</h3>
                    <div className="flex gap-3 mb-3">
                      {PIGMENTS_PRODUCTS.pigmentPaste.colors.map(color => {
                        const isSelected = pigmentSelections.pigmentPaste.some(p => p.color === color);
                        return (
                          <button
                            key={color}
                            onClick={() => isSelected ? removePigmentPaste(color) : addPigmentPaste(color)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              isSelected
                                ? 'bg-[#7A0B7A] text-white'
                                : 'bg-gray-100 hover:bg-[#7A0B7A]/10'
                            }`}
                          >
                            {color}
                          </button>
                        );
                      })}
                    </div>
                    {pigmentSelections.pigmentPaste.map(paste => (
                      <div key={paste.color} className="bg-gray-50 rounded-xl p-4 mb-2">
                        <h4 className="font-medium mb-2">{paste.color} Pigment Paste</h4>
                        <div className="space-y-2">
                          {PIGMENTS_PRODUCTS.pigmentPaste.packs.map(packOption => {
                            const pack = paste.packs.find(p => p.size === packOption.size);
                            const qty = pack ? pack.quantity : 0;
                            return (
                              <div key={packOption.size} className={`p-4 rounded-lg ${qty > 0 ? 'bg-white border-2 border-[#7A0B7A]/20' : 'bg-white/50'}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-[#27292E]">{packOption.size}m² ({packOption.description})</span>
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => updatePigmentPastePackQty(paste.color, packOption.size, -1)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"><Minus className="w-4 h-4" /></button>
                                    <span className="w-8 text-center font-bold text-lg">{qty}</span>
                                    <button onClick={() => updatePigmentPastePackQty(paste.color, packOption.size, 1)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white"><Plus className="w-4 h-4" /></button>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500">£{packOption.price.toFixed(2)} each</span>
                                  {qty > 0 && <span className="font-bold text-[#7A0B7A]">Total: £{(qty * packOption.price).toFixed(2)}</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Glitter */}
                  <div className="mb-6">
                    <h3 className="font-bold text-[#27292E] mb-3 pb-2 border-b">Glitter</h3>
                    <div className="flex gap-3 mb-3">
                      {PIGMENTS_PRODUCTS.glitter.types.map(type => {
                        const isSelected = pigmentSelections.glitter.some(g => g.type === type);
                        return (
                          <button
                            key={type}
                            onClick={() => isSelected ? removeGlitter(type) : addGlitter(type)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              isSelected
                                ? 'bg-[#7A0B7A] text-white'
                                : 'bg-gray-100 hover:bg-[#7A0B7A]/10'
                            }`}
                          >
                            {type}
                          </button>
                        );
                      })}
                    </div>
                    {pigmentSelections.glitter.map(glitter => (
                      <div key={glitter.type} className="bg-gray-50 rounded-xl p-4 mb-2">
                        <h4 className="font-medium mb-2">{glitter.type}</h4>
                        <div className="space-y-2">
                          {PIGMENTS_PRODUCTS.glitter.packs.map(packOption => {
                            const pack = glitter.packs.find(p => p.size === packOption.size);
                            const qty = pack ? pack.quantity : 0;
                            return (
                              <div key={packOption.size} className={`p-4 rounded-lg ${qty > 0 ? 'bg-white border-2 border-[#7A0B7A]/20' : 'bg-white/50'}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-[#27292E]">{packOption.size}m² pack</span>
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => updateGlitterPackQty(glitter.type, packOption.size, -1)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"><Minus className="w-4 h-4" /></button>
                                    <span className="w-8 text-center font-bold text-lg">{qty}</span>
                                    <button onClick={() => updateGlitterPackQty(glitter.type, packOption.size, 1)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white"><Plus className="w-4 h-4" /></button>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500">£{packOption.price.toFixed(2)} each</span>
                                  {qty > 0 && <span className="font-bold text-[#7A0B7A]">Total: £{(qty * packOption.price).toFixed(2)}</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Sprays */}
                  <div>
                    <h3 className="font-bold text-[#27292E] mb-3 pb-2 border-b">Sprays</h3>
                    
                    {/* Marble Spray */}
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-700 mb-2">Marble Spray</h4>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {PIGMENTS_PRODUCTS.sprays.marbleSpray.colors.map(color => (
                          <button
                            key={color}
                            onClick={() => addSpray('marble', color)}
                            className="px-3 py-1 text-sm bg-gray-100 hover:bg-[#7A0B7A]/10 rounded-lg transition-colors"
                          >
                            {color}
                          </button>
                        ))}
                      </div>
                      {pigmentSelections.marbleSprays.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                          {pigmentSelections.marbleSprays.map(spray => (
                            <div key={spray.color} className="p-4 bg-white rounded-lg border-2 border-[#7A0B7A]/20">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-[#27292E]">{spray.color}</span>
                                <div className="flex items-center gap-2">
                                  <button onClick={() => updateSprayQty('marble', spray.color, -1)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"><Minus className="w-4 h-4" /></button>
                                  <span className="w-8 text-center font-bold text-lg">{spray.quantity}</span>
                                  <button onClick={() => updateSprayQty('marble', spray.color, 1)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white"><Plus className="w-4 h-4" /></button>
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">£8.95 per 400ml</span>
                                <span className="font-bold text-[#7A0B7A]">Total: £{(spray.quantity * 8.95).toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Metallic Spray */}
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Metallic Spray</h4>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {PIGMENTS_PRODUCTS.sprays.metallicSpray.colors.map(color => (
                          <button
                            key={color}
                            onClick={() => addSpray('metallic', color)}
                            className="px-3 py-1 text-sm bg-gray-100 hover:bg-[#7A0B7A]/10 rounded-lg transition-colors"
                          >
                            {color}
                          </button>
                        ))}
                      </div>
                      {pigmentSelections.metallicSprays.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                          {pigmentSelections.metallicSprays.map(spray => (
                            <div key={spray.color} className="p-4 bg-white rounded-lg border-2 border-[#7A0B7A]/20">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-[#27292E]">{spray.color}</span>
                                <div className="flex items-center gap-2">
                                  <button onClick={() => updateSprayQty('metallic', spray.color, -1)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"><Minus className="w-4 h-4" /></button>
                                  <span className="w-8 text-center font-bold text-lg">{spray.quantity}</span>
                                  <button onClick={() => updateSprayQty('metallic', spray.color, 1)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white"><Plus className="w-4 h-4" /></button>
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">£8.95 per 400ml</span>
                                <span className="font-bold text-[#7A0B7A]">Total: £{(spray.quantity * 8.95).toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step 4 Subtotal */}
                {getPigmentsSubtotal() > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm p-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">Pigments & Effects Subtotal</span>
                      <span className="font-bold text-[#7A0B7A] text-lg">£{getPigmentsSubtotal().toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Running Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getItemCount()} item{getItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                    </div>
                    <p className="text-3xl font-bold">
                      £{calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => goToStep(4)}
                    variant="outline"
                    className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(6)}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
